var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Connection } from '../../Connection';
import { EventEmitter } from 'events';
import { TvWorkspaceManagerState } from '../TradingViewPrimitives/Workspace/TvWorkspaceManagerState';
import { TvSettingsData } from '../Settings/TvSettingsData';
import { TvSettingsAdapter } from '../Settings/TvSettingsAdapter';
import { ConnectionStates } from '@shared/utils/Connections/connection.types';
import { TvWorkspaceProvider } from '../Providers/TvWorkspaceProvider';
import { DataCacheHolder } from '../../cache/DataCacheHolder';
export var TvSaveWorkspaceMode;
(function (TvSaveWorkspaceMode) {
    TvSaveWorkspaceMode[TvSaveWorkspaceMode["None"] = 0] = "None";
    TvSaveWorkspaceMode[TvSaveWorkspaceMode["IndexedDB"] = 1] = "IndexedDB";
    TvSaveWorkspaceMode[TvSaveWorkspaceMode["Server"] = 2] = "Server";
    TvSaveWorkspaceMode[TvSaveWorkspaceMode["Both"] = 3] = "Both"; // IndexedDB | Server
})(TvSaveWorkspaceMode || (TvSaveWorkspaceMode = {}));
export class _TvWorkspaceManager {
    constructor() {
        this.tvSettingsAdapter = new TvSettingsAdapter();
        this._eventEmitter = new EventEmitter();
        this.state = TvWorkspaceManagerState.Unknown;
        this.chartSettings = null;
        this.theme = 'light';
    }
    initialize() {
        this.connectionStateChange = this.connectionStateChange.bind(this);
        Connection.onGetToken.Subscribe(this.onGetToken, this);
        Connection.subscribeOnConnectStateChange(this.connectionStateChange);
    }
    getState() {
        return this.state;
    }
    getChartSettings() {
        return this.chartSettings;
    }
    subscribeLoadWorkspace(callback) {
        this._eventEmitter.on('loaded', callback);
    }
    unsubscribeLoadWorkspace(callback) {
        this._eventEmitter.off('loaded', callback);
    }
    subscribeSaveWorkspace(callback) {
        this._eventEmitter.on('save', callback);
    }
    unsubscribeSaveWorkspace(callback) {
        this._eventEmitter.off('save', callback);
    }
    saveWorkSpace(data, mode) {
        return __awaiter(this, void 0, void 0, function* () {
            const savedData = new TvSettingsData();
            savedData.TvChartSettings = data;
            savedData.TvSettings = this.tvSettingsAdapter.initialSettings;
            savedData.defaultResolution = this.defaultResolution;
            savedData.defaultAccountId = this.defaultAccountId;
            savedData.userLogin = DataCacheHolder.getDataCache().UserLogin;
            savedData.theme = this.theme;
            savedData.time = Date.now();
            if ((mode & TvSaveWorkspaceMode.Server) !== 0) {
                yield TvWorkspaceProvider.saveWorkSpaceOnServer(savedData);
            }
            if ((mode & TvSaveWorkspaceMode.IndexedDB) !== 0) {
                yield TvWorkspaceProvider.saveWorkSpaceToIndexedDb(savedData);
            }
        });
    }
    setDefaultAccountId(accountId) {
        this.defaultAccountId = accountId;
    }
    getDefaultAccountId() {
        return this.defaultAccountId;
    }
    setDefaultResolution(resolution) {
        this.defaultResolution = resolution;
    }
    getDefaultResolution() {
        return this.defaultResolution;
    }
    setTheme(theme) {
        if (this.theme !== theme) {
            document.documentElement.classList.remove('dark', 'light');
            document.documentElement.classList.add(theme);
            this.theme = theme;
        }
    }
    getTheme() {
        return this.theme;
    }
    isOrdersPanelOpen() {
        return this.tvSettingsAdapter['trading.tradingPanelOpened'] === 'true';
    }
    loadWorkSpace() {
        return __awaiter(this, void 0, void 0, function* () {
            this.state = TvWorkspaceManagerState.Loading;
            this.clearPrevSettings();
            const settingsData = yield TvWorkspaceProvider.getWorkSpace();
            if (!isNullOrUndefined(settingsData)) {
                this.chartSettings = settingsData.TvChartSettings;
                this.defaultResolution = settingsData.defaultResolution;
                this.defaultAccountId = settingsData.defaultAccountId;
                this.setTheme(settingsData.theme);
                if (!isNullOrUndefined(settingsData.TvSettings)) {
                    this.tvSettingsAdapter.initialSettings = settingsData.TvSettings;
                }
            }
            else {
                this.clearLocalStorage();
            }
            this.state = TvWorkspaceManagerState.Loaded;
        });
    }
    onGetToken() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.loadWorkSpace();
            this.callOnLoaded();
        });
    }
    callOnLoaded() {
        this._eventEmitter.emit('loaded');
    }
    callOnSave(mode) {
        this._eventEmitter.emit('save', mode);
    }
    connectionStateChange() {
        return __awaiter(this, void 0, void 0, function* () {
            const state = Connection.getState();
            switch (state) {
                case ConnectionStates.CONNECTED:
                    TvWorkspaceProvider.reinitDatabase();
                    this.startInterval();
                    break;
                case ConnectionStates.DISCONNECTED:
                    this.stopInterval();
                    break;
            }
        });
    }
    startInterval() {
        const tenMinutes = 1000 * 60 * 10;
        this.serverIntervalId = setInterval(() => {
            this.saveWorkspaceToServerByTimer();
        }, tenMinutes);
        const oneMinute = 1000 * 60;
        this.indexedDbIntervalId = setInterval(() => {
            this.saveWorkspaceToIndexedDbByTimer();
        }, oneMinute);
    }
    stopInterval() {
        if (!isNullOrUndefined(this.serverIntervalId)) {
            clearInterval(this.serverIntervalId);
        }
        if (!isNullOrUndefined(this.indexedDbIntervalId)) {
            clearInterval(this.indexedDbIntervalId);
        }
    }
    saveWorkspaceToServerByTimer() {
        this.callOnSave(TvSaveWorkspaceMode.Server);
    }
    saveWorkspaceToIndexedDbByTimer() {
        this.callOnSave(TvSaveWorkspaceMode.IndexedDB);
    }
    clearLocalStorage() {
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (!isNullOrUndefined(key) && key.startsWith('tradingview.')) {
                localStorage.removeItem(key);
            }
        }
    }
    clearPrevSettings() {
        this.chartSettings = null;
        this.defaultResolution = null;
        this.defaultAccountId = null;
        this.theme = 'light';
        this.tvSettingsAdapter.clear();
    }
}
export const TvWorkspaceManager = new _TvWorkspaceManager();
